/**
 * 픽업 · 준비중 (PrepScreen) — 주문 접수 직후 준비 대기 화면(#794).
 *
 * @see #781 / admin#2111 설계 §7 / task-9 브리프
 */
import { Clock, MessageCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import type { Locale } from '@/config/locales';
import type { WaitlistPartyData, WaitlistStatusData } from '@/lib/api/waitlist';
import { WaitlistAppBar } from './WaitlistParts';
import type { WaitlistStrings } from './strings';

interface WaitlistPrepScreenProps {
  t: WaitlistStrings;
  storeName: string;
  storeZone: string;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  storeMeta?: WaitlistStatusData['store_meta'];
  party: WaitlistPartyData;
}

export function WaitlistPrepScreen({
  t,
  storeName,
  storeZone,
  locale,
  onLocaleChange,
  storeMeta,
  party,
}: WaitlistPrepScreenProps) {
  return (
    <>
      <WaitlistAppBar
        storeName={storeName}
        subtitle={storeZone}
        locale={locale}
        onLocaleChange={onLocaleChange}
        languageLabel={t.idioma}
        logoUrl={storeMeta?.logo_url}
        mapsUrl={storeMeta?.maps_url}
      />
      <div className="flex-1 overflow-y-auto">
        <div className="px-5 pb-10 pt-1">
          <Badge variant="secondary" className="mb-3">
            {t.pkPrepBadge}
          </Badge>
          <h1 className="m-0 mb-1 text-[26px] font-extrabold leading-tight tracking-[-0.02em]">
            {t.pkPrepTitle}
          </h1>
          <p className="m-0 mb-5 text-sm leading-relaxed text-muted-foreground">{t.pkPrepLead}</p>
          <Card className="mb-4">
            <CardContent className="p-5 text-center">
              <div className="text-[12.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                {t.pkOrderNumLabel}
              </div>
              <div className="text-[52px] font-extrabold leading-[1.15] tracking-[-0.02em] text-primary">
                {/* order_no 는 카운터가 접수해야 채워진다 — 없으면 "#undefined" 대신
                    ticket_no(등록 시 즉시 발급)로 대체한다(#794 Task 9 코드리뷰 시정). */}
                <bdi dir="ltr">#{party.order_no ?? party.ticket_no}</bdi>
              </div>
              {party.pickup_prep_minutes != null && (
                <div className="mt-3.5 inline-flex items-center gap-[7px] rounded-full bg-background-secondary px-3.5 py-2 text-[13px] font-semibold">
                  <Clock className="size-4" /> {t.pkPrepEta(party.pickup_prep_minutes)}
                </div>
              )}
            </CardContent>
          </Card>
          <div className="flex gap-2.5 rounded-lg bg-background-secondary px-3.5 py-3 text-[13px] leading-relaxed text-muted-foreground">
            <span className="shrink-0 text-primary">
              <MessageCircle className="size-[17px]" />
            </span>
            <span>{t.tip}</span>
          </div>
        </div>
      </div>
    </>
  );
}
